import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { api } from '../../services/api';
import { Pool } from '../../types/pool';

interface PoolTeamEntry {
  poolId: string;
  teamId: string;
  seed: number | null;
  metadata: any;
  team: {
    id: string;
    name: string;
    shortName: string;
    sport: string;
    conference: string | null;
  };
}

export function AdminTeamEditor() {
  const { poolId } = useParams<{ poolId: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const [pool, setPool] = useState<Pool | null>(null);
  const [teams, setTeams] = useState<PoolTeamEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editShortName, setEditShortName] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRegion, setFilterRegion] = useState('');

  useEffect(() => {
    if (poolId) loadData();
  }, [poolId]);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const [poolData, teamsRes] = await Promise.all([
        api.getPool(poolId!),
        api.getAdminPoolTeams(poolId!, token),
      ]);
      setPool(poolData);
      setTeams(teamsRes.teams || []);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  function startEdit(team: PoolTeamEntry) {
    setEditingTeamId(team.team.id);
    setEditName(team.team.name);
    setEditShortName(team.team.shortName);
  }

  function cancelEdit() {
    setEditingTeamId(null);
    setEditName('');
    setEditShortName('');
  }

  async function handleSave(teamId: string) {
    if (!editName.trim()) {
      alert('Team name cannot be empty');
      return;
    }

    try {
      setSaving(true);
      const token = await getAccessTokenSilently();
      await api.updateTeamName(teamId, {
        name: editName.trim(),
        shortName: editShortName.trim() || editName.trim(),
      }, token);

      // Update local state
      setTeams(prev =>
        prev.map(pt =>
          pt.team.id === teamId
            ? { ...pt, team: { ...pt.team, name: editName.trim(), shortName: editShortName.trim() || editName.trim() } }
            : pt
        )
      );
      setEditingTeamId(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update team name');
    } finally {
      setSaving(false);
    }
  }

  // Get unique regions from metadata
  const regions = [...new Set(teams.map(t => t.metadata?.region).filter(Boolean))].sort();

  // Filter and sort teams
  const filteredTeams = teams.filter(t => {
    const matchesSearch = !searchQuery ||
      t.team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.team.shortName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRegion = !filterRegion || t.metadata?.region === filterRegion;
    return matchesSearch && matchesRegion;
  });

  // Group by region if regions exist
  const groupedByRegion = regions.length > 0;
  const teamsByRegion: Record<string, PoolTeamEntry[]> = {};
  if (groupedByRegion) {
    for (const team of filteredTeams) {
      const region = team.metadata?.region || 'Other';
      if (!teamsByRegion[region]) teamsByRegion[region] = [];
      teamsByRegion[region].push(team);
    }
    // Sort each region by seed
    for (const region of Object.keys(teamsByRegion)) {
      teamsByRegion[region].sort((a, b) => (a.seed || 99) - (b.seed || 99));
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-600">Loading teams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Teams</h1>
            <p className="text-sm text-gray-600 mt-1">
              {pool?.name || 'Pool'} — {teams.length} teams
            </p>
          </div>
          <Link
            to="/admin"
            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            ← Back to Admin
          </Link>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Tip:</strong> Use this page to update team names for play-in games. Set placeholder names like
            "TBD - Play-In Winner" before the tournament, then update with the actual team name once the game is decided.
            Changes apply everywhere the team appears (entries, leaderboard, bracket).
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-800">{error}</div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <input
            type="text"
            placeholder="Search teams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
          />
          {regions.length > 0 && (
            <select
              value={filterRegion}
              onChange={(e) => setFilterRegion(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Regions</option>
              {regions.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          )}
          <span className="text-sm text-gray-500 self-center">
            Showing {filteredTeams.length} of {teams.length} teams
          </span>
        </div>

        {/* Teams Table */}
        {groupedByRegion ? (
          Object.entries(teamsByRegion).sort().map(([region, regionTeams]) => (
            <div key={region} className="mb-8">
              <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                {region} Region
                <span className="text-sm font-normal text-gray-500">({regionTeams.length} teams)</span>
              </h2>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seed</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Short Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conference</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {regionTeams.map(pt => renderTeamRow(pt))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {teams.some(t => t.seed) && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seed</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Short Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sport</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTeams
                  .sort((a, b) => (a.seed || 99) - (b.seed || 99) || a.team.name.localeCompare(b.team.name))
                  .map(pt => renderTeamRow(pt))}
              </tbody>
            </table>
          </div>
        )}

        {filteredTeams.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-500">
            No teams found. {searchQuery || filterRegion ? 'Try adjusting your filters.' : 'Import teams first.'}
          </div>
        )}
      </div>
    </div>
  );

  function renderTeamRow(pt: PoolTeamEntry) {
    const isEditing = editingTeamId === pt.team.id;

    return (
      <tr key={pt.team.id} className={isEditing ? 'bg-yellow-50' : 'hover:bg-gray-50'}>
        {(teams.some(t => t.seed) || groupedByRegion) && (
          <td className="px-4 py-3 text-sm">
            {pt.seed && (
              <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full font-bold text-sm">
                {pt.seed}
              </span>
            )}
          </td>
        )}
        <td className="px-4 py-3 text-sm">
          {isEditing ? (
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave(pt.team.id);
                if (e.key === 'Escape') cancelEdit();
              }}
            />
          ) : (
            <span className="font-medium text-gray-900">{pt.team.name}</span>
          )}
        </td>
        <td className="px-4 py-3 text-sm">
          {isEditing ? (
            <input
              type="text"
              value={editShortName}
              onChange={(e) => setEditShortName(e.target.value)}
              className="w-full px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave(pt.team.id);
                if (e.key === 'Escape') cancelEdit();
              }}
            />
          ) : (
            <span className="text-gray-600">{pt.team.shortName}</span>
          )}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">
          {groupedByRegion ? (pt.team.conference || '-') : pt.team.sport}
        </td>
        <td className="px-4 py-3 text-sm text-center">
          {isEditing ? (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => handleSave(pt.team.id)}
                disabled={saving}
                className="px-3 py-1 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={cancelEdit}
                className="px-3 py-1 text-xs font-semibold text-gray-600 hover:text-gray-800 border border-gray-300 rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => startEdit(pt)}
              className="px-3 py-1 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
            >
              Edit Name
            </button>
          )}
        </td>
      </tr>
    );
  }
}
